import { nanoid } from 'nanoid'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLazyAutocompleteQuery, useLazyPlaceDetailsQuery } from 'src/redux/api'
import { PlaceResult } from 'src/redux/slices/googleApiSlice'
import { MySelect } from '../ui'
import { MySelectProps } from '../ui/select/MySelect'
import { useAppSelector } from 'src/redux/hooks'

interface Props extends Omit<MySelectProps, 'options' | 'name'> {
  onPlaceSelect: (place: any | null) => void
  search: string[]
  reduxValue: any | null
  bounds?: PlaceResult['geometry']['viewport']
}

export const AutocompleteCustom = ({
  onPlaceSelect,
  search,
  reduxValue,
  error,
  ...props
}: Props) => {
  const { t } = useTranslation()
  const [, setInputValue] = useState('')
  const [, setSelectedPlaceId] = useState<string | null>(null)
  const { center, radius } = useAppSelector((state) => state.googleApi)

  // The place already chosen (a draft, an edited listing) is shown as the
  // value; it used to replace the placeholder, so a filled field looked grey
  // like an empty one and an empty field lost its own hint. Memoised: the
  // select re-syncs whenever the value object changes.
  const chosen = useMemo(
    () =>
      reduxValue?.name
        ? { value: reduxValue.place_id ?? reduxValue.name, label: reduxValue.name }
        : undefined,
    [reduxValue?.name, reduxValue?.place_id],
  )

  // Дебаунсим ручками, потому что RTK Query не умеет дебаунсить автоматом
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null)

  // Сохраняем sessionToken на сессию
  const sessionTokenRef = useRef(nanoid())

  // RTK Query "ленивая" версия — используем только когда нужно (по debounce)
  const [triggerAutocomplete, { data: predictions = [], isFetching, isError }] =
    useLazyAutocompleteQuery()

  // handleInputChange с debounce
  const handleInputChange = (value: string) => {
    setInputValue(value)

    if (debounceTimeout.current) clearTimeout(debounceTimeout.current)

    if (!value) {
      setSelectedPlaceId(null)
      return
    }
    let restriction: any = undefined
    if (center && radius && !search.includes('locality')) {
      restriction = {
        circle: {
          center: {
            latitude: center.lat,
            longitude: center.lng,
          },
          radius: radius,
        },
      }
    }

    debounceTimeout.current = setTimeout(() => {
      triggerAutocomplete({
        input: value,
        sessionToken: sessionTokenRef.current,
        locationRestriction: JSON.stringify(restriction),
        types: search,
      })
    }, 500)
  }

  // Запрашиваем детали, если выбран placeId
  const [getPlaceDetails] = useLazyPlaceDetailsQuery()

  return (
    <div className="autocomplete-container relative w-full">
      <MySelect
        name="google"
        isDefault
        {...props}
        value={chosen}
        isSearchable
        error={error}
        // inputValue={inputValue}
        isLoading={isFetching}
        // An address service outage (Google billing/key) must read as a state
        // of this field, not only as a toast that has already faded.
        noOptionsMessage={isError ? () => t('errors.PLACES_UNAVAILABLE') : undefined}
        onInputChange={(value, action) => {
          if (action.action === 'input-change') handleInputChange(value)
        }}
        onChange={async (option) => {
          setSelectedPlaceId(option.value)
          try {
            const details = await getPlaceDetails({
              place_id: option.value,
              sessionToken: sessionTokenRef.current,
            }).unwrap()
            onPlaceSelect(details)
          } catch {
            // Reported by the global toast middleware; nothing to select.
          }
        }}
        options={predictions.map(({ place_id, description }: any) => ({
          value: place_id,
          label: description,
        }))}
      />
    </div>
  )
}
